import { TagValidationUtils } from 'tag/ts/smart_tag/operations/types/TagValidationUtils';
import { TTagRemoveData } from 'tag/ts/smart_tag/types/api/TTagRemoveData';
import { TTagRemoveReject } from 'tag/ts/smart_tag/types/api/TTagRemoveReject';
import { TTagRemoveResolve } from 'tag/ts/smart_tag/types/api/TTagRemoveResolve';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { unregisterAviaErrorHandler } from 'tag/ts/tag/player/adapters/avia/utilities/AviaErrorHandler';
import { ISmartTagOperationsDependencies } from './types/SmartTagOperationsTypes';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Handles individual tag removal operations with comprehensive error handling
 *
 * **INDIVIDUAL TAG REMOVAL STRATEGY:**
 * Implements robust single-tag removal with defensive programming patterns. Shared/global
 * resources are preserved while other tags remain active, and torn down only when removal
 * empties the registry (the last tag).
 *
 * **Key Memory Management Principle:**
 * While other tags remain, individual removal does NOT touch global resources — they are still
 * needed. When the registry becomes empty, the shared globals are torn down: the ConnectionMonitor
 * (destroy) and the avia error handler (unregisterAviaErrorHandler). Both are re-armed on the next
 * create() (see SmartTag.create), so a later tag still gets connectivity events and avia rejection
 * suppression.
 *
 * **Cleanup Scope:**
 * - ✅ Remove individual tag from registry/list
 * - ✅ Clean up tag-specific resources
 * - ✅ Tear down shared globals (ConnectionMonitor, avia error handler) only when the registry empties
 * - ❌ Do NOT touch shared/global resources while other tags remain
 *
 * **Contrast with Reset Operations:**
 * Reset processes ALL tags together and cleans up globals on complete success; individual removal
 * does the same only in the last-tag (empty-registry) case.
 *
 * Ensures graceful degradation when system utilities are partially unavailable.
 */
export class SmartTagRemovalOperations {
	#deps: ISmartTagOperationsDependencies;

	constructor( deps: ISmartTagOperationsDependencies ) {
		this.#deps = deps;
	}

	/**
	 * @description Remove Tag instance with proper error handling
	 * @param {ITag} tag
	 * @return {Promise<TTagRemoveData>}
	 */
	removeTag = ( tag: ITag ): Promise<TTagRemoveData> => {
		this.#deps.logger.log( 'remove:', tag );

		return new Promise( async ( resolve: TTagRemoveResolve, reject: TTagRemoveReject ) => {
			// Early validation
			const validation = TagValidationUtils.validateTagForRemoval( tag, this.#deps.utilities );
			if ( !validation.isValid ) {
				reject( { msg: validation.error } );
				return;
			}

			// Execute tag reset and cleanup
			await this.#executeTagRemoval( tag, resolve, reject );
		} );
	};

	/**
	 * @description Orchestrates tag removal with comprehensive error handling
	 * Manages the complete removal lifecycle including reset, cleanup, and finalization
	 * while providing detailed error context for debugging and monitoring.
	 */
	async #executeTagRemoval( tag: ITag, resolve: TTagRemoveResolve, reject: TTagRemoveReject ): Promise<void> {
		try {
			const data = await tag.API.TAG.reset();
			this.#deps.logger.log( 'remove:', 'Tag.reset():', data, 'Done!' );
			await this.#cleanupRemovedTag( tag, resolve, reject );
		} catch ( error ) {
			this.#deps.logger.warn( 'remove:', 'Tag.reset() failed:', error );
			// Force collection cleanup even when reset() rejects — otherwise the tag is stranded
			// in the registry/list and its resources are never released (leak on repeated removals).
			this.#forceCollectionCleanup( tag );
			reject( { msg: 'Failure: Tag reset failed.', error } );
		}
	}

	/**
	 * @description Defensively removes the tag from the Smart Tag list and registry. Used on the
	 * reset-failure path so a failed teardown does not leave the tag stranded in tracking
	 * collections. When removal empties the registry, also tears down the ConnectionMonitor and
	 * avia error handler — mirroring the empty-registry cleanup in the success path. Swallows its
	 * own errors to guarantee the reject path still completes.
	 * @param {ITag} tag
	 */
	#forceCollectionCleanup( tag: ITag ): void {
		try {
			this.#deps.removeFromList( tag.uuid );
			this.#deps.removeFromRegistry( tag.uuid );

			if ( Object.keys( this.#deps.getRegistry() || {} ).length === 0 ) {
				this.#deps.getConnectionMonitor()?.destroy();
				this.#deps.setConnectionMonitor( undefined );
				unregisterAviaErrorHandler();
				this.#deps.logger.log( 'remove:', 'Registry empty after forced cleanup - connection monitor + avia error handler torn down' );
			}
		} catch ( cleanupError ) {
			this.#deps.logger.warn( 'remove:', 'Error during forced collection cleanup:', cleanupError );
		}
	}

	/**
	 * @description Defensive cleanup with graceful degradation for individual tag removal.
	 *
	 * **GLOBAL RESOURCE CLEANUP IS CONDITIONAL:**
	 * While other tags remain in the registry, this method deliberately does NOT touch global
	 * resources (the avia error handler, ConnectionMonitor) — other tags still need them, and
	 * removing them while Avia players are active would cause console error spam from Avia library
	 * race conditions. Only when this removal empties the registry (the last tag) are the shared
	 * globals torn down — there is then no remaining tag or Avia player that needs them. They are
	 * re-armed on the next create() (see SmartTag.create).
	 *
	 * **Cleanup scope:**
	 * - Always: remove the tag from the Smart Tag registry/list; clean up tag-specific resources
	 * - While other tags remain: do NOT touch global/shared resources
	 * - When the registry becomes empty: tear down ConnectionMonitor + the avia error handler
	 *
	 * **Contrast with Reset Operations:**
	 * Reset processes ALL tags together and cleans up globals on complete success; individual
	 * removal reaches the same clean state only in the last-tag (empty-registry) case.
	 *
	 * Continues operation even if some utilities are unavailable, ensuring registry cleanup occurs
	 * even when list operations fail. Implements safe property access patterns to prevent null
	 * pointer exceptions.
	 */
	async #cleanupRemovedTag( tag: ITag, resolve: TTagRemoveResolve, reject: TTagRemoveReject ): Promise<void> {
		try {
			// Safe access with proper validation
			if ( !this.#deps.utilities?.Array?.indexOf ) {
				this.#deps.logger.warn( 'remove:', 'Array utilities not available for cleanup' );
				await this.#finalizeRemoval( resolve, reject );
				return;
			}

			const list = this.#deps.getList();
			if ( !list ) {
				this.#deps.logger.warn( 'remove:', 'List not available for cleanup' );
				await this.#finalizeRemoval( resolve, reject );
				return;
			}

			const indexToDelete = this.#deps.utilities.Array.indexOf( 'uuid', tag.uuid, list );

			if ( indexToDelete !== null && indexToDelete !== undefined ) {
				this.#deps.removeFromList( tag.uuid );
			}

			// Registry removal is unconditional — mirrors the reset path and ensures teardown
			// fires even when the list is out of sync and indexOf returns null.
			this.#deps.removeFromRegistry( tag.uuid );
			this.#deps.logger.log( 'remove:', `Tag ${tag.uuid} removed from collections` );

			// If this was the last tag, tear down the shared globals — safe only because the registry
			// is now empty (no remaining tag needs them, and with no tag there is no avia player to
			// emit rejections). Both are re-armed on the next create() once the registry is non-empty
			// again (SmartTag.create), so a later tag still gets connectivity events + rejection suppression:
			//   - ConnectionMonitor: online/offline window listeners + Firefox polling interval
			//   - avia error handler: the global unhandledrejection suppressor
			if ( Object.keys( this.#deps.getRegistry() || {} ).length === 0 ) {
				this.#deps.getConnectionMonitor()?.destroy();
				this.#deps.setConnectionMonitor( undefined );
				unregisterAviaErrorHandler();
				this.#deps.logger.log( 'remove:', 'Registry empty - connection monitor + avia error handler torn down' );
			}

			await this.#finalizeRemoval( resolve, reject );
		} catch ( cleanupError ) {
			this.#deps.logger.warn( 'remove:', 'Error during cleanup operations:', cleanupError );
			reject( { msg: 'Failure: Error during tag cleanup operations.', error: cleanupError } );
		}
	}

	/**
	 * @description Completes tag removal with optional delay handling
	 * Provides graceful fallback when Delay utilities are unavailable,
	 * ensuring operation completion regardless of utility availability.
	 */
	async #finalizeRemoval( resolve: TTagRemoveResolve, reject: TTagRemoveReject ): Promise<void> {
		if ( this.#deps.utilities?.Delay?.delay ) {
			try {
				await this.#deps.utilities.Delay.delay();
				this.#deps.logger.log( 'remove:', 'inform the app!' );
				resolve( { msg: 'Success: tag was removed.' } );
			} catch ( delayError ) {
				this.#deps.logger.warn( 'remove:', 'Delay.delay() failed:', delayError );
				resolve( { msg: 'Success: tag was removed (delay warning).' } );
			}
		} else {
			this.#deps.logger.log( 'remove:', 'inform the app!' );
			resolve( { msg: 'Success: tag was removed.' } );
		}
	}
}